import {
  ArrowDownIcon,
  CameraIcon,
  CheckCircleIcon,
  CheckIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import FindingUserImpact from "@/components/audits/FindingUserImpact";
import type { Finding, FindingPriority, Interaction, UsabilityEvaluationRollup } from "@/types";
import { titleize } from "@/utils/formatters";

type FindingWithContext = Finding & {
  viewport: string | null;
  screenshotImageUrl: string | null;
};

export default function UsabilityEvaluationSection({
  evaluation,
  interactions,
}: {
  evaluation: UsabilityEvaluationRollup;
  interactions: Interaction[];
}) {
  const findings = interactions.flatMap((interaction) =>
    (interaction.usability_evaluation?.findings || []).map((finding) => ({
      ...finding,
      viewport: interaction.viewport,
      screenshotImageUrl:
        interaction.screenshots.find((screenshot) => screenshot.id === finding.screenshot_id)?.image_url || null,
    })),
  );
  const progressPercentage =
    evaluation.total_interactions > 0
      ? Math.round(
          ((evaluation.completed_usability_evaluations + evaluation.failed_usability_evaluations) /
            evaluation.total_interactions) *
            100,
        )
      : 0;
  const completed = evaluation.status === "completed";
  const groupedFindings = groupFindingsByPriority(findings);

  return (
    <section className="mt-6">
      <div className={`mb-6 overflow-hidden rounded-lg border shadow-sm ${statusContainerClasses(evaluation)}`}>
        <div className="p-4">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="flex items-center">
              <div className="relative">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full shadow-sm ${statusIconClasses(evaluation)}`}
                >
                  <StatusIcon evaluation={evaluation} />
                </div>
                {completed && evaluation.issue_count === 0 ? (
                  <div className="absolute -inset-1 animate-ping rounded-full bg-green-400 opacity-20" />
                ) : null}
              </div>
              <div className="ml-4">
                <h2
                  className={`mb-0 flex items-center text-lg font-semibold ${completed && evaluation.issue_count === 0 ? "text-green-900" : "text-gray-900"}`}
                >
                  {statusTitle(evaluation)}
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClasses(evaluation)}`}
                  >
                    {statusBadgeText(evaluation)}
                  </span>
                </h2>
                <p
                  className={`mt-0.5 text-sm ${completed && evaluation.issue_count === 0 ? "text-green-700" : "text-gray-600"}`}
                >
                  {statusSubtext(evaluation)}
                </p>
              </div>
            </div>

            <div className="text-left md:text-right">
              {completed ? (
                evaluation.issue_count > 0 ? (
                  <a
                    href="#usability-issues"
                    className="inline-flex items-center rounded-lg bg-gradient-to-r from-orange-600 to-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:from-orange-600 hover:to-red-700 hover:shadow-md"
                  >
                    <DocumentTextIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                    View audit
                    <ArrowDownIcon className="ml-1 h-3 w-3" aria-hidden="true" />
                  </a>
                ) : (
                  <div className="inline-flex items-center rounded-lg bg-green-100 px-4 py-2 text-sm font-medium text-green-700">
                    <SparklesIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                    Excellent!
                  </div>
                )
              ) : (
                <div className="w-48">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Progress</span>
                    <span className="text-sm font-bold text-gray-900">{progressPercentage}%</span>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-gray-200 shadow-inner">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 shadow-sm"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <div className="mt-1 text-center text-xs text-gray-500">Analyzing usability</div>
                </div>
              )}
            </div>
          </div>

          {evaluation.failed_usability_evaluations > 0 ? (
            <p className="mt-3 text-sm text-red-700">
              {evaluation.failed_usability_evaluations}{" "}
              {evaluation.failed_usability_evaluations === 1
                ? "interaction analysis failed"
                : "interaction analyses failed"}
              .
            </p>
          ) : null}
        </div>
      </div>

      <div id="usability-issues" className="mb-8 scroll-m-6">
        <h2 className="mb-4 text-2xl font-semibold text-gray-800">Usability Issues</h2>
        {!completed ? (
          <div className="card mx-auto w-full">
            <div className="card-body py-10 text-center">
              <h2 className="mb-2 text-xl font-semibold text-gray-900">Analyzing usability...</h2>
              <div className="mx-auto mb-5 mt-3 h-2 w-90 max-w-full rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-primary transition-[width]"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="mx-auto max-w-md text-gray-500">
                We are evaluating the UX of this interaction with Artificial Intelligence. Usability issues will appear
                here once the analysis is complete.
              </p>
            </div>
          </div>
        ) : findings.length > 0 ? (
          <div className="space-y-12">
            {priorityOrder.map((priority) =>
              groupedFindings[priority]?.length ? (
                <div key={priority} className={priorityGroupClasses(priority)}>
                  <h3 className={`mb-6 flex items-center gap-2 text-lg font-semibold ${priorityTextClasses(priority)}`}>
                    <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
                    {priorityGroupLabel(priority)}
                    <span className="ml-2 font-normal text-gray-400">({groupedFindings[priority].length})</span>
                  </h3>
                  <div className="space-y-4">
                    <div className="flex flex-col gap-y-4 divide-y divide-gray-100">
                      {groupedFindings[priority].map((finding) => (
                        <FindingCard key={finding.id} finding={finding} />
                      ))}
                    </div>
                  </div>
                </div>
              ) : null,
            )}
          </div>
        ) : (
          <div className="card mx-auto w-full">
            <div className="card-body py-10 text-center">
              <div className="mb-6 flex items-center justify-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success">
                  <CheckIcon className="h-10 w-10 text-white" aria-hidden="true" />
                </div>
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">No usability issues detected</h2>
              <p className="text-gray-500">We did not find any usability issues for this interaction.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function FindingCard({ finding }: { finding: FindingWithContext }) {
  const [screenshotVisible, setScreenshotVisible] = useState(false);
  const viewportIcon =
    finding.viewport === "mobile" ? (
      <DevicePhoneMobileIcon className="mr-1 h-3 w-3" aria-hidden="true" />
    ) : (
      <ComputerDesktopIcon className="mr-1 h-3 w-3" aria-hidden="true" />
    );

  return (
    <article
      id={`audit-issue-${finding.id}`}
      className="group relative rounded-r-lg border border-gray-100 bg-white px-5 py-4"
    >
      <div
        className={`absolute bottom-0 left-0 top-0 w-[2px] opacity-60 group-hover:opacity-100 ${priorityIndicatorClasses(finding.priority)}`}
      />
      <div className="flex items-start justify-between pl-1 md:pl-3">
        <div className="flex flex-1 flex-col items-start space-y-3">
          <div className="flex w-full flex-col items-start justify-between gap-y-2 lg:flex-row lg:items-center lg:gap-x-2 lg:gap-y-0">
            <div className="flex flex-col items-start gap-x-2 gap-y-2 lg:flex-row lg:items-center lg:gap-y-0">
              {finding.heuristic?.name ? (
                <span className="block text-xs font-semibold uppercase text-gray-500">{finding.heuristic.name}</span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {finding.viewport ? (
                <span className="flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                  {viewportIcon}
                  {titleize(finding.viewport)}
                </span>
              ) : null}
            </div>
          </div>
          <h4 className="mb-2 text-base font-semibold text-gray-800">{finding.title || "Untitled issue"}</h4>
          {finding.description ? (
            <p className="mb-3 text-sm leading-relaxed text-gray-700">{finding.description}</p>
          ) : null}
          {finding.user_impact ? <FindingUserImpact userImpact={finding.user_impact} /> : null}
          {finding.suggestion ? (
            <div className="rounded-r-md border-l-4 border-gray-300 bg-gray-50 p-3">
              <div className="flex items-start">
                <LightBulbIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-600">Suggestion</p>
                  <p className="text-sm leading-relaxed text-gray-700">{finding.suggestion}</p>
                </div>
              </div>
            </div>
          ) : null}

          {finding.screenshotImageUrl ? (
            <>
              <button
                type="button"
                className="btn w-full text-sm"
                onClick={() => setScreenshotVisible((visible) => !visible)}
              >
                <CameraIcon className="h-4 w-4" aria-hidden="true" />
                {screenshotVisible ? "Hide screenshot" : "Show screenshot"}
              </button>
              {screenshotVisible ? (
                <div className="self-start rounded-lg border border-gray-200 bg-gray-50 p-2 lg:p-4">
                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <img
                      src={finding.screenshotImageUrl}
                      alt="Screenshot of the issue"
                      className="h-auto max-h-[667px]"
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function StatusIcon({ evaluation }: { evaluation: UsabilityEvaluationRollup }) {
  if (evaluation.status === "running" || evaluation.status === "pending") {
    return (
      <div className="relative h-6 w-6">
        <div className="absolute inset-0 rounded-full border-2 border-white/30" />
        <div className="absolute inset-0 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    );
  }

  if (evaluation.status === "completed" && evaluation.issue_count === 0) {
    return <CheckCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />;
  }

  return <ExclamationTriangleIcon className="h-5 w-5 text-white" aria-hidden="true" />;
}

function statusTitle(evaluation: UsabilityEvaluationRollup) {
  if (evaluation.status === "completed") return "Analysis Complete";
  if (evaluation.status === "failed") return "Analysis Failed";
  if (evaluation.status === "cancelled") return "Analysis Cancelled";

  return "AI Usability Analysis";
}

function statusBadgeText(evaluation: UsabilityEvaluationRollup) {
  if (evaluation.status === "completed") {
    if (evaluation.issue_count === 0) return "All Clear";

    return `${evaluation.issue_count} ${evaluation.issue_count === 1 ? "Issue" : "Issues"} Found`;
  }
  if (evaluation.status === "failed") return "Failed";
  if (evaluation.status === "cancelled") return "Cancelled";

  return "In Progress";
}

function statusSubtext(evaluation: UsabilityEvaluationRollup) {
  if (evaluation.status === "completed") {
    return evaluation.issue_count > 0
      ? "We identified usability issues that affect users"
      : "No significant usability issues found";
  }
  if (evaluation.status === "failed") return "We could not complete the usability analysis";
  if (evaluation.status === "cancelled") return "The usability analysis was cancelled";

  return "Analyzing your interface for usability issues";
}

function statusContainerClasses(evaluation: UsabilityEvaluationRollup) {
  if (evaluation.status === "completed" && evaluation.issue_count > 0)
    return "border-orange-200 bg-gradient-to-r from-orange-50 to-red-50";
  if (evaluation.status === "completed")
    return "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 transition-all duration-300";
  if (evaluation.status === "failed") return "border-red-100 bg-gradient-to-r from-red-50 to-orange-50";

  return "border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50";
}

function statusIconClasses(evaluation: UsabilityEvaluationRollup) {
  if (evaluation.status === "completed" && evaluation.issue_count > 0)
    return "bg-gradient-to-r from-orange-500 to-red-600";
  if (evaluation.status === "completed") return "bg-gradient-to-r from-green-500 to-emerald-600";
  if (evaluation.status === "failed") return "bg-gradient-to-r from-red-500 to-orange-600";

  return "bg-gradient-to-r from-blue-500 to-indigo-600";
}

function statusBadgeClasses(evaluation: UsabilityEvaluationRollup) {
  if (evaluation.status === "completed" && evaluation.issue_count > 0) return "bg-orange-100 text-orange-700";
  if (evaluation.status === "completed") return "bg-green-100 text-green-700";
  if (evaluation.status === "failed") return "bg-red-100 text-red-700";

  return "bg-blue-100 text-blue-700";
}

const priorityOrder: FindingPriority[] = ["critical", "high", "medium", "low"];

function groupFindingsByPriority(findings: FindingWithContext[]) {
  return findings
    .sort((first, second) => (second.severity || 0) - (first.severity || 0))
    .reduce<Record<FindingPriority, FindingWithContext[]>>(
      (grouped, finding) => {
        grouped[finding.priority].push(finding);

        return grouped;
      },
      { critical: [], high: [], medium: [], low: [] },
    );
}

function priorityGroupLabel(priority: FindingPriority) {
  if (priority === "critical" || priority === "high") return "High priority";
  if (priority === "medium") return "Medium priority";

  return "Low priority";
}

function priorityTextClasses(priority: FindingPriority) {
  if (priority === "critical" || priority === "high") return "text-error-dark";
  if (priority === "medium") return "text-warning-dark";

  return "text-primary-700";
}

function priorityGroupClasses(priority: FindingPriority) {
  if (priority === "critical" || priority === "high") return "border-error-light";
  if (priority === "medium") return "border-warning-light";

  return "border-primary-50";
}

function priorityIndicatorClasses(priority: FindingPriority) {
  if (priority === "critical" || priority === "high") return "bg-error";
  if (priority === "medium") return "bg-warning";

  return "bg-primary";
}
